import { PointAddResponse } from "@1games-ru/region-shared/dist/client/requests/point";
import { InteriorType } from "@1games-ru/region-shared/dist/enums/interior/interior";
import { InteriorComplexFloor } from "@1games-ru/region-shared/dist/types/interior/interior-b2";
import { Point as WorldPoint } from "@1games-ru/region-shared/dist/types/world/world";
import { Dispatch, SetStateAction, useCallback, useId, useState } from "react";
import { CheckIcon } from "@/shared/icons";
import { Button, TextInput } from "@/shared/ui";
import { Point } from "@/domains/createEntity/constants/point";
import getInitPoint from "@/domains/createEntity/helpers/getInitPoint";
import { pointToPoint } from "@/domains/createEntity/helpers/pointToPoint";
import toast from "@/domains/toasts/helpers/toast";
import useGetInteriors from "@/domains/createEntity/hooks/useGetInteriors";
import usePoint from "@/domains/createEntity/hooks/usePoint";
import type { InteriorFormProps } from "@/domains/createEntity/containers/forms/interior/CrateEntityInteriorFormContainer";
import CreateEntityColumn from "@/domains/createEntity/components/CreateEntityColumn";
import CreateEntityRow from "@/domains/createEntity/components/CreateEntityRow";
import CreateEntitySetLocation from "@/domains/createEntity/components/CreateEntitySetLocation";
import CreateEntitySlider from "@/domains/createEntity/components/CreateEntitySlider";

export default function CreateEntityInteriorComplexFloorFormContainer({
  onSubmit,
  onClear,
}: InteriorFormProps<InteriorComplexFloor>) {
  const id = useId();
  const { addPoint } = usePoint();
  const [uuid, setUuid] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [zone, setZone] = useState<number | null>(30);
  const [height, setHeight] = useState<number | null>(1);
  const [point, setPoint] = useState<PointAddResponse | null>(null);

  const [elevatorsPoints, setElevatorsPoints] = useState<PointAddResponse[]>([]);
  const [exitsPoints, setExitsPoints] = useState<PointAddResponse[]>([]);
  const [roomsPoints, setRoomsPoints] = useState<PointAddResponse[]>([]);
  const [signsPoints, setSignsPoints] = useState<PointAddResponse[]>([]);

  const { getInteriors } = useGetInteriors(InteriorType.ComplexFloor);

  const addPointToArray = useCallback(
    async (
      pointType: Point,
      setter: Dispatch<SetStateAction<PointAddResponse[]>>,
      additionalProps?: Record<string, unknown>,
    ) => {
      const p = await addPoint({
        ...getInitPoint(pointType, { id }),
        ...additionalProps,
      });
      setter((prev) => [...prev, p]);
      return p;
    },
    [addPoint, id],
  );

  const clearPoints = useCallback(() => {
    setZone(30);
    setHeight(1);
    setUuid(null);
    setPoint(null);
    setName(null);
    setDescription(null);
    setElevatorsPoints([]);
    setExitsPoints([]);
    setRoomsPoints([]);
    setSignsPoints([]);
    onClear();
  }, [onClear]);

  const createAndSetPoint = useCallback(
    async (
      pointType: Point,
      setter: (point: PointAddResponse | null) => void,
      additionalProps?: Record<string, unknown>,
    ) => {
      const p = await addPoint({
        ...getInitPoint(pointType, { id }),
        ...additionalProps,
      });
      setter(p);
      return p;
    },
    [addPoint, id],
  );

  const createAndSetPointArray = useCallback(
    async (
      pointType: Point,
      setter: Dispatch<SetStateAction<PointAddResponse[]>>,
      points: WorldPoint[],
    ) => {
      const promises = points.map(({ position }) =>
        addPoint({
          ...getInitPoint(pointType, { id }),
          position,
        }),
      );
      const p = await Promise.all(promises);
      setter(p);
      return p;
    },
    [addPoint, id],
  );

  async function handleSubmit() {
    if (
      !height ||
      !name ||
      !description ||
      !point ||
      !elevatorsPoints.length ||
      !exitsPoints.length ||
      !roomsPoints.length ||
      !signsPoints.length
    ) {
      toast.error("Заполните все поля");
      return;
    }

    await onSubmit({
      uuid,
      name,
      description,
      content: {
        height: 10,
        border: {
          range: zone,
          position: point?.position,
        },
        elevators: elevatorsPoints.map((vehPoint) => pointToPoint(vehPoint)),
        exits: exitsPoints.map((vehPoint) => pointToPoint(vehPoint)),
        rooms: roomsPoints.map((vehPoint) => pointToPoint(vehPoint)),
        signs: signsPoints.map((vehPoint) => pointToPoint(vehPoint)),
      },
    });
    clearPoints();
  }

  async function handleEdit() {
    const interiors = await getInteriors();
    const interior = interiors.find((item) => item.uuid === uuid);
    if (!interior) {
      toast.error("Этаж не найден");
      return;
    }

    const content = interior.content as InteriorComplexFloor;

    setName(interior.name);
    setDescription(interior.description);
    setZone(content.border.range);

    createAndSetPoint(Point.InteriorLocation, setPoint, {
      range: content.border.range,
      position: content.border.position,
    });

    createAndSetPointArray(Point.ElevatorLocation, setElevatorsPoints, content.elevators);
    createAndSetPointArray(Point.ExitLocation, setExitsPoints, content.exits);
    createAndSetPointArray(Point.RoomLocation, setRoomsPoints, content.rooms);
    createAndSetPointArray(Point.SignLocation, setSignsPoints, content.signs);
  }

  return (
    <>
      <Button variant="solid-outline" onClick={clearPoints}>
        Очистить точки
      </Button>

      <CreateEntityRow label="Редактировать этаж">
        <div className="flex gap-4">
          <TextInput
            placeholder="UUID этажа"
            value={uuid}
            onChange={(value) => setUuid(value)}
            className="w-full"
          />
          <Button onClick={() => handleEdit()} disabled={!uuid}>
            <CheckIcon size={16} />
          </Button>
        </div>
      </CreateEntityRow>

      <CreateEntityRow label="Высота этажа">
        <TextInput
          value={String(height)}
          onChange={(value) => setHeight(Number(value))}
          placeholder="Высота этажа"
        />
      </CreateEntityRow>

      <CreateEntityRow label="Название интерьера">
        <TextInput value={name} onChange={setName} placeholder="Придумайте название" />
      </CreateEntityRow>

      <CreateEntityRow label="Описание интерьера">
        <TextInput
          value={description}
          onChange={setDescription}
          placeholder="Придумайте описание"
        />
      </CreateEntityRow>

      <CreateEntityRow label="Размер зоны интерьера">
        <CreateEntitySlider
          value={zone}
          min={10}
          max={50}
          step={10}
          values={[
            {
              label: "10",
              value: 10,
            },
            {
              label: "20",
              value: 20,
            },
            {
              label: "30",
              value: 30,
            },
            {
              label: "40",
              value: 40,
            },
            {
              label: "50",
              value: 50,
            },
          ]}
          onChange={setZone}
          className="px-1"
          classNameSlider="cursor-pointer"
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение этажа">
        <CreateEntitySetLocation
          is={!!point}
          onChange={async () => {
            setPoint(await addPoint(getInitPoint(Point.InteriorLocation, { range: zone, id })));
          }}
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение лифтов">
        <div className="flex flex-col gap-1.5">
          {elevatorsPoints.map((vehPoint, index) => (
            <CreateEntityColumn
              key={vehPoint.id}
              title="Лифт"
              onDelete={() =>
                setElevatorsPoints((prev) => prev.filter((p) => p.id !== vehPoint.id))
              }
              index={index + 1}
            />
          ))}

          <Button
            data-mz-accent="success"
            className="w-full flex items-center justify-between h-[2.625rem] shrink-0 mt-1"
            onClick={() => addPointToArray(Point.ElevatorLocation, setElevatorsPoints)}
          >
            Добавить лифт
          </Button>
        </div>
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение выходов">
        <div className="flex flex-col gap-1.5">
          {exitsPoints.map((vehPoint, index) => (
            <CreateEntityColumn
              key={vehPoint.id}
              title="Выход"
              onDelete={() => setExitsPoints((prev) => prev.filter((p) => p.id !== vehPoint.id))}
              index={index + 1}
            />
          ))}

          <Button
            data-mz-accent="success"
            className="w-full flex items-center justify-between h-[2.625rem] shrink-0 mt-1"
            onClick={() => addPointToArray(Point.ExitLocation, setExitsPoints)}
          >
            Добавить выход
          </Button>
        </div>
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение комнат">
        <div className="flex flex-col gap-1.5">
          {roomsPoints.map((vehPoint, index) => (
            <CreateEntityColumn
              key={vehPoint.id}
              title="Комната"
              onDelete={() => setRoomsPoints((prev) => prev.filter((p) => p.id !== vehPoint.id))}
              index={index + 1}
            />
          ))}

          <Button
            data-mz-accent="success"
            className="w-full flex items-center justify-between h-[2.625rem] shrink-0 mt-1"
            onClick={() => addPointToArray(Point.RoomLocation, setRoomsPoints)}
          >
            Добавить комнату
          </Button>
        </div>
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение вывесок">
        <div className="flex flex-col gap-1.5">
          {signsPoints.map((vehPoint, index) => (
            <CreateEntityColumn
              key={vehPoint.id}
              title="Вывеска"
              onDelete={() => setSignsPoints((prev) => prev.filter((p) => p.id !== vehPoint.id))}
              index={index + 1}
            />
          ))}

          <Button
            data-mz-accent="success"
            className="w-full flex items-center justify-between h-[2.625rem] shrink-0 mt-1"
            onClick={() => addPointToArray(Point.SignLocation, setSignsPoints)}
          >
            Добавить вывеску
          </Button>
        </div>
      </CreateEntityRow>

      <Button onClick={() => handleSubmit()}>Сохранить интерьер</Button>
    </>
  );
}
